// Booking related types and interfaces

// Basic transport type definition
export interface TransportType {
  id: number;
  name: string;
  abbreviation: string;
}

// Basic booking type definition
export interface BookingType {
  id: number;
  name: string;
}

// Company interface
export interface Company {
  id: number;
  created_at?: string;
  name: string;
  status?: string;
  country?: string;
  city?: string;
  postal_code?: string;
  street?: string;
  house_number?: string;
  vat_number?: string;
  eori_number?: string;
  coc_number?: number;
  article_23?: boolean;
  phone_number?: string;
  created_by_user_id?: string;
  pricing_payment_term?: number;
  address?: string; // May be composite or standalone
  moneybird_contact_id?: string; // Moneybird contact ID for this company
}

// Supplier interface
export interface Supplier {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  country?: string;
  province?: string;
}

// Port interface
export interface Port {
  id: number;
  name: string;
  country?: string;
}

// Incoterm interface
export interface Incoterm {
  id: number;
  name: string;
  description?: string;
}

// Agent interface
export interface Agent {
  id: number;
  name: string;
  email?: string;
  phone?: string;
}

// Product interface
export interface Product {
  id: number;
  name: string;
  hs_code?: string | null;
  ean?: string | null;
  pieces?: number | null;
  cartons?: number | null;
  gross_weight: number;
  cbm: number;
  value?: number | null;
  shipping_label?: string | null;
  booking_id: number;
}

// Delivery information interface
export interface DeliveryInfo {
  id: number;
  street_name?: string;
  house_number?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  // New fields for delivery workflow
  delivery_cartons?: number;
  delivery_pallets?: number;
  cartons_per_pallet?: number;
  lmd_total_cost?: number;
  lmd_calculation_method?: 'auto' | 'carton' | 'manual';
  shipment_status?: 'planned' | 'ready' | 'shipped' | 'delivered';
  scheduled_delivery_date?: string;
  shipment_prepared_at?: string;
  shipment_shipped_at?: string;
}

// Container interface
export interface Container {
  id: number;
  created_at: string;
  updated_at?: string;
  transport_type: number | null;
  status: string | null;
  reference: string | null;
  etd: string | null;
  eta: string | null;
  eta_customer: string | null;
  track_and_trace: string | null;
  origin_port: number | null;
  destination_port: number | null;
  bl_number: string | null;
  
  // Include related info
  origin_port_info?: Port | Port[];
  destination_port_info?: Port | Port[];
  container_origin_port?: Port | Port[];
  container_destination_port?: Port | Port[];
  master_container_info?: {
    master_container_id: number;
    master_containers: {
      id: number;
      reference: string | null;
    };
  };
  
  // Related bookings
  bookings?: Booking[];
}

// Invoice line interface
export interface InvoiceLine {
  quantity: number;
  unit_price: number;
  vat_percentage: number;
}

// Invoice info interface for booking queries
export interface InvoiceInfo {
  id: number;
  created_at: string;
  currency: string;
  moneybird_payment_status?: string | null;
  moneybird_invoice_date?: string | null;
  lines?: InvoiceLine[];
}

// Main booking interface
export interface Booking {
  id: number;
  created_at: string;
  status: string | null;
  eta: string | null;
  reference: string | null;
  transport_type: number | null;
  company_id: number | null;
  booking_type: number | null;
  origin_pickup_date: string | null;
  booking_incoterm: number | null;
  commercial_invoice_file: string | null;
  supplier_id: number | null;
  origin_port: number | null;
  destination_port: number | null;
  agent_id: number | null;
  rejection_reason: string | null;
  container_id: number | null;
  confirmed_pick_up_date: string | null;
  origin_received_date: string | null;
  notes: string | null;
  // New unloading workflow fields
  unloading_status?: 'pending' | 'in_progress' | 'completed';
  unloaded_at?: string;
  total_actual_cartons?: number;
  total_actual_pallets?: number;
  unloading_notes?: string;
  
  // Include related info - can be either direct objects or arrays depending on query
  transport_type_info?: TransportType | TransportType[];
  booking_type_info?: BookingType | BookingType[];
  company_info?: Company | Company[];
  supplier_info?: Supplier | Supplier[];
  origin_port_info?: Port | Port[];
  destination_port_info?: Port | Port[];
  booking_incoterm_info?: Incoterm | Incoterm[];
  agent_info?: Agent | Agent[];
  product_info?: Product[] | Product[][];
  booking_delivery_info?: DeliveryInfo | DeliveryInfo[];
  container_info?: Container | Container[];
  invoice_info?: InvoiceInfo | InvoiceInfo[];
}

// Pagination params interface
export interface PaginationParams {
  page: number;
  pageSize: number;
  searchQuery?: string;
  status?: string;
  dateRange?: [string, string] | null;
  sortField?: string;
  sortOrder?: "ascend" | "descend";
  companyId?: string | null;
  transportTypeId?: string | null;
  isConsolidation?: boolean | null;
  containerId?: string | null;
  agentId?: string | null;
  bookingTypeId?: string | null;
  blNumber?: string | null;
  masterContainerId?: string | null;
  hasInvoice?: boolean | null;
  deliveryType?: 'storage' | 'delivery' | null;
}

// Status count interface
export interface StatusCount {
  status: string;
  count: number;
}

export enum BookingStatus {
  WAITING_FOR_GOODS = "waiting_for_goods",
  CANCELLED = "cancelled",
  OUT_FOR_DELIVERY = "out_for_delivery",
  CONCEPT = "concept",
  ADDED_TO_STOCK = "added_to_stock",
  GOODS_RECEIVED_ORIGIN = "goods_received_origin",
  APPROVED = "approved",
  IN_TRANSIT = "in_transit",
  CUSTOM_CLEARANCE = "custom_clearance",
  ARCHIVED = "archived",
  REJECTED = "rejected",
  DELIVERED = "delivered",
  SUBMITTED = "submitted"
}

export const bookingStatusLabels: Record<BookingStatus, string> = {
  [BookingStatus.WAITING_FOR_GOODS]: "Waiting for Goods",
  [BookingStatus.CANCELLED]: "Cancelled",
  [BookingStatus.OUT_FOR_DELIVERY]: "Out for Delivery",
  [BookingStatus.CONCEPT]: "Concept",
  [BookingStatus.ADDED_TO_STOCK]: "Added to Stock",
  [BookingStatus.GOODS_RECEIVED_ORIGIN]: "Goods Received at Origin",
  [BookingStatus.APPROVED]: "Approved",
  [BookingStatus.IN_TRANSIT]: "In Transit",
  [BookingStatus.CUSTOM_CLEARANCE]: "Custom Clearance",
  [BookingStatus.ARCHIVED]: "Archived",
  [BookingStatus.REJECTED]: "Rejected",
  [BookingStatus.DELIVERED]: "Delivered",
  [BookingStatus.SUBMITTED]: "Pending Approval"
};

// Master Container types
export interface MasterContainer {
  id: number;
  created_at: string;
  reference: string | null;
  eta_customer: string | null;
  eta: string | null;
  etd: string | null;
  status: string | null;
  track_and_trace: string | null;
}

export interface MasterContainerContainer {
  id: number;
  created_at: string;
  container_id: number | null;
  master_container_id: number | null;
}

export interface MasterContainerWithContainers extends MasterContainer {
  containers?: Container[];
  container_count?: number;
} 